/**
 * Telegram Mini App native UX wrapper (2026-05-11, Mini-App-native pass).
 *
 * Purpose: give TG-native users (those who opened @roamzy_bot Mini App) the
 * full set of Telegram WebApp UI primitives — MainButton, BackButton,
 * HapticFeedback, showPopup/showConfirm — so the experience feels like a
 * proper Mini App, not a website embedded in WebView.
 *
 * Design contract:
 *   - EVERY exported function is a no-op outside Telegram. Web users see
 *     nothing change. The guard is centralised here (single source of truth
 *     for `isTelegramWebApp()`), and version-checks each TG API individually
 *     so older TG clients (where some APIs don't exist yet) get graceful
 *     degradation — our own buttons / modals remain on the page and continue
 *     to work.
 *   - We DON'T sync TG theme colors to our CSS variables. Operator burned
 *     by this 2026-05-09 (TG dark mode → pure black bg → paper bg
 *     became black → text invisible). Brand stays brand.
 *   - We DON'T integrate Telegram Stars / Telegram Payments. Operator
 *     explicit 2026-05-11.
 *   - We DON'T use CloudStorage / BiometricManager. Out of scope.
 *
 * Module structure:
 *   - `tgIsAvailable()`            — single guard (covers initData presence)
 *   - `mainButton.*`               — show/hide/setText/onClick/loading
 *   - `backButton.*`               — show/hide/onClick
 *   - `haptic.*`                   — tap, success, error, warning, selection
 *   - `tgPopup.*`                  — confirm, alert (Promise-based)
 *   - `closingConfirmation.*`      — enable/disable for mid-flow guards
 *   - `viewport.*`                 — height, stableHeight, listen events
 *
 * Each handler stack (MainButton onClick, BackButton onClick) follows
 * push/pop semantics: callers register on mount, unregister on unmount.
 * Active handler = top of stack. This matches how our modal manager and
 * router push/pop UI layers — TG-native buttons must mirror that layer
 * order so BackButton always backs out of the topmost layer first.
 */

// --- Types we use from window.Telegram.WebApp ---------------------------
// The official TG SDK types aren't shipped as @types/ package; we define
// the subset we touch. All API calls are guarded by feature detection
// before invocation, so missing properties on older TG versions don't
// throw — they're just skipped.

interface TgMainButton {
  text: string;
  color?: string;
  textColor?: string;
  isVisible: boolean;
  isProgressVisible: boolean;
  isActive: boolean;
  setText(text: string): TgMainButton;
  onClick(cb: () => void): TgMainButton;
  offClick(cb: () => void): TgMainButton;
  show(): TgMainButton;
  hide(): TgMainButton;
  enable(): TgMainButton;
  disable(): TgMainButton;
  showProgress(leaveActive?: boolean): TgMainButton;
  hideProgress(): TgMainButton;
  setParams(params: {
    text?: string;
    color?: string;
    text_color?: string;
    is_active?: boolean;
    is_visible?: boolean;
  }): TgMainButton;
}

interface TgBackButton {
  isVisible: boolean;
  onClick(cb: () => void): TgBackButton;
  offClick(cb: () => void): TgBackButton;
  show(): TgBackButton;
  hide(): TgBackButton;
}

interface TgHapticFeedback {
  impactOccurred(style: 'light' | 'medium' | 'heavy' | 'rigid' | 'soft'): TgHapticFeedback;
  notificationOccurred(type: 'error' | 'success' | 'warning'): TgHapticFeedback;
  selectionChanged(): TgHapticFeedback;
}

interface TgSettingsButton {
  isVisible: boolean;
  onClick(cb: () => void): TgSettingsButton;
  offClick(cb: () => void): TgSettingsButton;
  show(): TgSettingsButton;
  hide(): TgSettingsButton;
}

interface TgPopupButton {
  id?: string;
  type?: 'default' | 'ok' | 'close' | 'cancel' | 'destructive';
  text?: string;
}

interface TgWebApp {
  initData?: string;
  version?: string;
  platform?: string;
  viewportHeight?: number;
  viewportStableHeight?: number;
  isExpanded?: boolean;
  MainButton?: TgMainButton;
  BackButton?: TgBackButton;
  HapticFeedback?: TgHapticFeedback;
  SettingsButton?: TgSettingsButton;
  ready?: () => void;
  expand?: () => void;
  close?: () => void;
  enableClosingConfirmation?: () => void;
  disableClosingConfirmation?: () => void;
  showPopup?: (
    params: { title?: string; message: string; buttons?: TgPopupButton[] },
    cb?: (id: string) => void,
  ) => void;
  showAlert?: (message: string, cb?: () => void) => void;
  showConfirm?: (message: string, cb?: (ok: boolean) => void) => void;
  openLink?: (url: string, options?: { try_instant_view?: boolean }) => void;
  openTelegramLink?: (url: string) => void;
  onEvent?: (event: string, cb: () => void) => void;
  offEvent?: (event: string, cb: () => void) => void;
}

// NOTE: `Window.Telegram` is already declared in `./telegram-webapp.ts` with
// a minimal TelegramWebApp interface (initData/ready/expand only). To avoid
// "Subsequent property declarations must have the same type" we DON'T
// re-declare the global here — instead we cast inside `getTg()` to our
// richer `TgWebApp` shape. The runtime is the same `window.Telegram.WebApp`
// object the TG SDK script populates; the two interfaces just describe
// different (overlapping) slices of it.

// --- Core guard --------------------------------------------------------

/**
 * True when the page is running inside a Telegram Mini App context
 * (`@roamzy_bot` → opened our site as a Web App).
 *
 * Detection signal: `window.Telegram.WebApp.initData` is non-empty.
 * The SDK populates this on construction with a signed query string
 * derived from the bot user's Telegram session. Empty/absent on
 * regular browsers (no `window.Telegram` namespace) and inside the
 * Telegram client when opened as an external link (not a Web App).
 *
 * Mirrors `isInTelegram()` in `telegram-webapp.ts` — same predicate;
 * we re-export for callers that import the rest of this module.
 */
export function tgIsAvailable(): boolean {
  const tg = getTg();
  return !!(tg && tg.initData && tg.initData.length > 0);
}

function getTg(): TgWebApp | null {
  // Cast through unknown because `window.Telegram.WebApp` is typed in
  // telegram-webapp.ts as a narrower interface (initData/ready/expand
  // only). The runtime object has every TG SDK field; we just describe
  // a richer slice here for the MainButton/BackButton/Haptic/Popup
  // APIs this module owns.
  const w = window as Window & { Telegram?: { WebApp?: unknown } };
  return (w.Telegram?.WebApp as TgWebApp | undefined) ?? null;
}

/**
 * Brand color for the MainButton. Our accent is the only "Pay/Get eSIM"
 * indicator the user sees at the bottom of TG — keep it stable across
 * routes so muscle memory works.
 *
 * The brand's primary CTA colour, --accent-deep in styles/tokens.css (the
 * shade .btn--accent uses, white text passes AA). It used to be a blue
 * #1F70F1 "matching --brand-accent" — a token that no longer exists, so every
 * Mini App user saw a blue native button under a terracotta site (2026-09-29).
 * Hard-coded rather than CSS-var read because TG MainButton API wants a
 * hex string at the moment of `setParams`, not a live computed style.
 */
const MAIN_BUTTON_COLOR = '#C84B1F';
const MAIN_BUTTON_TEXT_COLOR = '#ffffff';

// --- MainButton stack --------------------------------------------------

interface MainButtonConfig {
  text: string;
  /** Called on tap. Async return allowed — we'll show loading until it settles. */
  onClick: () => void | Promise<void>;
  /** Disable until prerequisite met (e.g. form invalid). Default: enabled. */
  enabled?: boolean;
  /** Hide instead of disable. Default: false (button visible but greyed). */
  hidden?: boolean;
}

interface MainButtonFrame extends MainButtonConfig {
  /** Wrapper that bridges TG.onClick → our async handler with loading state. */
  wrapped: () => void;
}

const mainButtonStack: MainButtonFrame[] = [];

function applyMainButtonTop(): void {
  const tg = getTg();
  const btn = tg?.MainButton;
  if (!tg || !btn) return;
  const top = mainButtonStack[mainButtonStack.length - 1];
  if (!top) {
    // No active frame — clear the button entirely.
    try {
      btn.hide();
    } catch {
      /* old TG */
    }
    return;
  }
  try {
    btn.setParams({
      text: top.text,
      color: MAIN_BUTTON_COLOR,
      text_color: MAIN_BUTTON_TEXT_COLOR,
      is_active: top.enabled !== false,
      is_visible: top.hidden !== true,
    });
  } catch {
    // Older TG: setParams missing → fallback to discrete calls.
    try {
      btn.setText(top.text);
      if (top.enabled === false) btn.disable();
      else btn.enable();
      if (top.hidden) btn.hide();
      else btn.show();
    } catch {
      /* give up silently */
    }
  }
}

/**
 * Push a new MainButton frame onto the stack. Returns an `unmount` function
 * the caller MUST invoke when the surface that owns the button (route, modal)
 * is torn down — that pops the frame and restores the previous one.
 *
 * Stack semantics (vs. just "set"):
 *   - Country page mounts: pushes "Get eSIM"
 *   - User opens topup modal on top: pushes "Pay $20" — modal owns the
 *     button now
 *   - Modal closes (unmount): pops back to "Get eSIM"
 *   - User navigates away: route unmounts → pops the country frame → no
 *     active frame → button hides
 *
 * Outside Telegram (web) the call is a no-op and returns a no-op unmount.
 * This keeps caller code uniform: every surface calls
 * `mainButton.push({...})` regardless of platform.
 */
export const mainButton = {
  push(config: MainButtonConfig): () => void {
    if (!tgIsAvailable()) return () => undefined;
    const tg = getTg();
    const btn = tg?.MainButton;
    if (!tg || !btn) return () => undefined;

    let busy = false;
    const wrapped = (): void => {
      // Re-entrancy guard: if the previous tap's async handler hasn't
      // resolved yet, ignore further taps. TG fires this once per tap,
      // but double-tap from haptic-confused users does happen.
      if (busy) return;
      busy = true;
      // Visual: progress spinner ON. `leaveActive=true` keeps button
      // tappable visually but our `busy` flag short-circuits the work.
      try {
        btn.showProgress(true);
      } catch {
        /* old TG, no progress */
      }
      Promise.resolve()
        .then(() => config.onClick())
        .catch((err) => {
          if (import.meta.env.DEV) console.warn('[tg-main-button] handler error', err);
        })
        .finally(() => {
          busy = false;
          try {
            btn.hideProgress();
          } catch {
            /* old TG */
          }
        });
    };

    // CRITICAL: TG `MainButton.onClick(cb)` is ADDITIVE — registering a
    // new callback does NOT replace the previous one. If we just added
    // every frame's wrapped, ALL of them would fire on tap (in registration
    // order). With a stack of frames that's wrong: only the visually-top
    // frame's onClick should execute. So before pushing, we detach the
    // current top's wrapped; on pop, we re-attach the now-top's wrapped.
    //
    // This bit us 2026-05-11 (operator caught from country page): country
    // route's frame stayed active under the topup modal's frame, both
    // fired on MainButton tap. Country's wrapped re-opened the topup
    // modal (cta.click → openTopUp → ModalManager.close-then-reopen),
    // destroying the form DOM mid-submit; modal's wrapped then fired
    // form.requestSubmit() on a now-detached form → submit silently
    // failed → no payment redirect. Dashboard worked accidentally
    // because add-new-esim has an async reservation-guard that bails
    // before re-opening the modal.
    const previousTop = mainButtonStack[mainButtonStack.length - 1];
    if (previousTop) {
      try { btn.offClick(previousTop.wrapped); } catch { /* old TG */ }
    }

    const frame: MainButtonFrame = { ...config, wrapped };
    mainButtonStack.push(frame);
    try {
      btn.onClick(wrapped);
    } catch {
      /* old TG */
    }
    applyMainButtonTop();

    return (): void => {
      const idx = mainButtonStack.lastIndexOf(frame);
      if (idx >= 0) mainButtonStack.splice(idx, 1);
      try {
        btn.offClick(wrapped);
      } catch {
        /* old TG */
      }
      // Re-attach the now-top frame's wrapped (if any). Without this,
      // popping a modal's frame would leave the route's frame visually
      // active but functionally dead (offClick'd when WE pushed on top
      // of it; never re-attached).
      const newTop = mainButtonStack[mainButtonStack.length - 1];
      if (newTop) {
        try { btn.onClick(newTop.wrapped); } catch { /* old TG */ }
      }
      applyMainButtonTop();
    };
  },

  /**
   * Update the currently-active frame's text / enabled. Used when the
   * inside-modal form state changes ("Pay $20" → "Pay $50" as user
   * types). No-op if no active frame.
   */
  update(patch: Partial<Pick<MainButtonConfig, 'text' | 'enabled' | 'hidden'>>): void {
    if (!tgIsAvailable()) return;
    const top = mainButtonStack[mainButtonStack.length - 1];
    if (!top) return;
    if (patch.text !== undefined) top.text = patch.text;
    if (patch.enabled !== undefined) top.enabled = patch.enabled;
    if (patch.hidden !== undefined) top.hidden = patch.hidden;
    applyMainButtonTop();
  },
};

// --- BackButton stack --------------------------------------------------

interface BackButtonFrame {
  onClick: () => void;
}

const backButtonStack: BackButtonFrame[] = [];

function applyBackButtonTop(): void {
  const tg = getTg();
  const btn = tg?.BackButton;
  if (!tg || !btn) return;
  const top = backButtonStack[backButtonStack.length - 1];
  try {
    if (top) btn.show();
    else btn.hide();
  } catch {
    /* old TG */
  }
}

/**
 * Push a BackButton frame. Top frame's `onClick` wins. Returns unmount.
 *
 * Typical use:
 *   - Router pushes a frame that does `history.back()` on every route
 *     that's not '/'
 *   - When a modal opens it pushes its own frame that closes the modal;
 *     the modal close handler pops the frame
 *
 * This way TG BackButton always backs out of the topmost UI layer first:
 *   modal open + on /dashboard/esims → BackButton closes modal
 *   modal closed + on /dashboard/esims → BackButton goes to previous route
 */
export const backButton = {
  push(onClick: () => void): () => void {
    if (!tgIsAvailable()) return () => undefined;
    const tg = getTg();
    const btn = tg?.BackButton;
    if (!tg || !btn) return () => undefined;

    // Same swap-on-push pattern as MainButton — TG's BackButton.onClick
    // is also additive. Without this, tapping BackButton inside a modal
    // would fire BOTH the modal-close handler AND the route's
    // history.back handler — closing the modal AND navigating away in
    // one tap. We want strict top-only semantics: top frame wins.
    const previousTop = backButtonStack[backButtonStack.length - 1];
    if (previousTop) {
      try { btn.offClick(previousTop.onClick); } catch { /* old TG */ }
    }

    const frame: BackButtonFrame = { onClick };
    backButtonStack.push(frame);
    try {
      btn.onClick(onClick);
    } catch {
      /* old TG */
    }
    applyBackButtonTop();

    return (): void => {
      const idx = backButtonStack.lastIndexOf(frame);
      if (idx >= 0) backButtonStack.splice(idx, 1);
      try {
        btn.offClick(onClick);
      } catch {
        /* old TG */
      }
      // Re-attach the new top's handler after pop.
      const newTop = backButtonStack[backButtonStack.length - 1];
      if (newTop) {
        try { btn.onClick(newTop.onClick); } catch { /* old TG */ }
      }
      applyBackButtonTop();
    };
  },
};

// --- Haptic feedback ---------------------------------------------------

/**
 * Telegram haptic feedback — physical vibration on supported devices.
 *
 * Reference (UX choices):
 *   - `tap()`              — every CTA tap (primary action)
 *   - `tapLight()`         — secondary actions (toggle, pill, dropdown)
 *   - `tapHeavy()`         — destructive (delete, cancel, danger)
 *   - `success()` / `error()` / `warning()` — bind to async outcome
 *     (toast, API resolve/reject, validation)
 *   - `selection()`        — picker item change (Country picker, locale)
 *
 * No-op outside TG. Inside TG also no-op on platforms that don't
 * support haptics (e.g. desktop client) — the API itself is safe to
 * call, it just doesn't vibrate.
 */
export const haptic = {
  tap(): void {
    if (!tgIsAvailable()) return;
    try { getTg()?.HapticFeedback?.impactOccurred('medium'); } catch { /* old TG */ }
  },
  tapLight(): void {
    if (!tgIsAvailable()) return;
    try { getTg()?.HapticFeedback?.impactOccurred('light'); } catch { /* old TG */ }
  },
  tapHeavy(): void {
    if (!tgIsAvailable()) return;
    try { getTg()?.HapticFeedback?.impactOccurred('heavy'); } catch { /* old TG */ }
  },
  success(): void {
    if (!tgIsAvailable()) return;
    try { getTg()?.HapticFeedback?.notificationOccurred('success'); } catch { /* old TG */ }
  },
  error(): void {
    if (!tgIsAvailable()) return;
    try { getTg()?.HapticFeedback?.notificationOccurred('error'); } catch { /* old TG */ }
  },
  warning(): void {
    if (!tgIsAvailable()) return;
    try { getTg()?.HapticFeedback?.notificationOccurred('warning'); } catch { /* old TG */ }
  },
  selection(): void {
    if (!tgIsAvailable()) return;
    try { getTg()?.HapticFeedback?.selectionChanged(); } catch { /* old TG */ }
  },
};

// --- Native popups -----------------------------------------------------

/**
 * Native TG confirm dialog — replaces `window.confirm` in Mini App context.
 *
 * Returns Promise<boolean>: true on OK, false on cancel. Outside TG
 * falls back to `window.confirm` so callers don't need a separate path —
 * `if (!(await tgConfirm(text))) return;` works everywhere.
 */
export function tgConfirm(message: string): Promise<boolean> {
  if (!tgIsAvailable()) {
    return Promise.resolve(window.confirm(message));
  }
  const tg = getTg();
  if (!tg?.showConfirm) {
    // Older TG without showConfirm — fall back to browser confirm.
    // The Mini App still uses the system browser primitive but at least
    // it's not silently broken.
    return Promise.resolve(window.confirm(message));
  }
  return new Promise<boolean>((resolve) => {
    try {
      tg.showConfirm!(message, (ok: boolean) => {
        // Vibrate based on outcome — operator-driven UX want.
        if (ok) haptic.success();
        else haptic.tapLight();
        resolve(ok);
      });
    } catch {
      resolve(window.confirm(message));
    }
  });
}
// --- Closing confirmation ----------------------------------------------

/**
 * Enable/disable TG's "Close anyway?" prompt when user tries to dismiss
 * the Mini App with unsaved state. Used when:
 *   - Topup form has been filled (mid-flow, payment in progress)
 *   - Checkout is mid-request to NowPayments
 *
 * Reference-counted: nested callers (modal inside another flow) all
 * push refs; user only sees the prompt while at least one ref is active.
 */
let closingRefs = 0;

export const closingConfirmation = {
  enable(): () => void {
    if (!tgIsAvailable()) return () => undefined;
    const tg = getTg();
    closingRefs += 1;
    if (closingRefs === 1) {
      try { tg?.enableClosingConfirmation?.(); } catch { /* old TG */ }
    }
    return (): void => {
      closingRefs = Math.max(0, closingRefs - 1);
      if (closingRefs === 0) {
        try { tg?.disableClosingConfirmation?.(); } catch { /* old TG */ }
      }
    };
  },
};

// --- Viewport ----------------------------------------------------------

/**
 * Current visible viewport height inside the Mini App. On iOS this
 * shrinks when the keyboard appears. `stableHeight` is the height
 * WITHOUT the keyboard — what we'd render to if input weren't focused.
 *
 * Listening to `viewportChanged` events fires every time TG's internal
 * height changes (keyboard appear/disappear, drawer expand). Caller
 * uses this to recompute layout-critical heights like the modal's
 * max-height so the bottom button stays above the keyboard.
 */
export const viewport = {
  height(): number | null {
    const h = getTg()?.viewportHeight;
    return typeof h === 'number' ? h : null;
  },
  stableHeight(): number | null {
    const h = getTg()?.viewportStableHeight;
    return typeof h === 'number' ? h : null;
  },
  /**
   * Subscribe to viewport-changed event. Returns unsubscribe.
   * No-op outside TG.
   */
  onChange(cb: () => void): () => void {
    if (!tgIsAvailable()) return () => undefined;
    const tg = getTg();
    if (!tg?.onEvent) return () => undefined;
    try {
      tg.onEvent('viewportChanged', cb);
    } catch {
      return () => undefined;
    }
    return (): void => {
      try {
        tg.offEvent?.('viewportChanged', cb);
      } catch {
        /* old TG */
      }
    };
  },
};

// --- SettingsButton ----------------------------------------------------

/**
 * Wire TG's overflow-menu "Settings" item to a callback. Used to jump
 * directly to /dashboard/settings without the user having to find our
 * own settings icon in the (hidden) site nav.
 */
export const settingsButton = {
  show(onClick: () => void): () => void {
    if (!tgIsAvailable()) return () => undefined;
    const tg = getTg();
    const btn = tg?.SettingsButton;
    if (!tg || !btn) return () => undefined;
    try {
      btn.onClick(onClick);
      btn.show();
    } catch {
      /* old TG */
    }
    return (): void => {
      try {
        btn.offClick(onClick);
        btn.hide();
      } catch {
        /* old TG */
      }
    };
  },
};

// --- External link openers --------------------------------------------

/**
 * Open an external URL — uses TG's openLink when available so the link
 * opens in the user's system browser (not inside the Mini App WebView,
 * which would replace our session).
 */
export function tgOpenLink(url: string): void {
  if (!tgIsAvailable()) {
    window.open(url, '_blank', 'noopener,noreferrer');
    return;
  }
  const tg = getTg();
  try {
    if (tg?.openLink) tg.openLink(url, { try_instant_view: false });
    else window.open(url, '_blank', 'noopener,noreferrer');
  } catch {
    window.open(url, '_blank', 'noopener,noreferrer');
  }
}
